/**
 * Photos shared into Givi Den from another app, by the phone's Share menu.
 *
 * The manifest names Givi Den as somewhere photos and clips can be shared to.
 * The phone posts them to SHARE_ACTION; the service worker answers that post
 * by keeping them here and sending the app on to the chat; the chat takes
 * them on opening and puts them in its composer to wait for Send, the same
 * as photos picked from the plus. Android only: iOS offers web apps no place
 * in its Share menu.
 *
 * Kept in a cache rather than handed over in the redirect, because a photo
 * does not fit in a URL and the worker and the page share nothing else.
 */

export const SHARE_ACTION = '/share-target';
const SHARE_CACHE = 'givisnap-shared';
const TEXT_KEY = '/shared/text';

export interface Shared {
  files: File[];
  /** Any words shared with them -- a caption, a link -- to start the message. */
  text: string;
}

/** In the worker: keep what was shared until the app opens and takes it. */
export async function keepShared(form: FormData, store: CacheStorage): Promise<void> {
  const cache = await store.open(SHARE_CACHE);
  // A share nobody claimed is replaced by the next one, not added to it.
  await Promise.all((await cache.keys()).map((key) => cache.delete(key)));

  const files = form.getAll('media').filter((each): each is File => typeof each !== 'string');
  await Promise.all(
    files.map((file, index) =>
      cache.put(
        `/shared/${index}`,
        new Response(file, {
          headers: {
            'content-type': file.type || 'application/octet-stream',
            'x-name': encodeURIComponent(file.name),
          },
        }),
      ),
    ),
  );

  // Apps fill these in differently, and often say the same link twice.
  const words = [...new Set(
    ['title', 'text', 'url']
      .map((key) => form.get(key))
      .filter((each): each is string => typeof each === 'string' && each.trim() !== '')
      .map((each) => each.trim()),
  )].join(' ');
  if (words) await cache.put(TEXT_KEY, new Response(words));
}

/** In the app: what was shared, taken once. Null when nothing was. */
export async function takeShared(
  store: CacheStorage | undefined = globalThis.caches,
): Promise<Shared | null> {
  if (!store) return null;
  const cache = await store.open(SHARE_CACHE);
  const keys = await cache.keys();
  if (keys.length === 0) return null;

  let text = '';
  // A cache lists its keys in the order they were put, which is the order shared.
  const files: File[] = [];
  for (const key of keys) {
    const response = await cache.match(key);
    if (!response) continue;
    const path = new URL(key.url).pathname;
    if (path === TEXT_KEY) {
      text = await response.text();
      continue;
    }
    const blob = await response.blob();
    const name = decodeURIComponent(response.headers.get('x-name') ?? 'shared');
    files.push(new File([blob], name, { type: response.headers.get('content-type') ?? blob.type }));
  }
  await Promise.all(keys.map((key) => cache.delete(key)));

  return { files, text };
}
